import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/context/AuthContext";

const AFFIRMATIONS = [
  "You are stronger than the day you were diagnosed. Softly, steadily, you are healing.",
  "Your body is doing quiet, beautiful work — even the tired days count.",
  "One small act of self-care is not small at all. It is a love letter to your future self.",
  "Compression, elevation, breath. You know what you need — trust the practice.",
  "Progress isn't a straight line. It looks a lot like showing up, again and again.",
  "The way you care for yourself is the way you teach the world how to care for you.",
  "You do not have to earn rest. Rest is the medicine.",
  "Every entry is a whisper to future-you: I noticed. I remembered. I loved us both.",
  "Some days healing looks like a walk. Some days it looks like a nap. Both are holy.",
  "Your softness is not weakness — it is precisely how you carry so much.",
  "Swelling ebbs and returns like tides. You are the sturdy shore beneath.",
  "Being gentle with yourself is not giving up — it is showing up in a wiser way.",
  "You are the only person who has ever been you. That, alone, is a wonder.",
  "One breath. One sip of water. One kind thought. That's enough to begin again.",
  "Even on the hardest flare-up days, your light is still there. Just quieter.",
];

const LS_KEY = "lc_welcomed_on";

export default function WelcomeGreeting() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!user) return;
    // Don't stack with the first-time tour
    if (!localStorage.getItem("lc_tour_done_v1")) return;
    const today = format(new Date(), "yyyy-MM-dd");
    const last = localStorage.getItem(LS_KEY);
    if (last === today) return;
    const pick = AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)];
    setMessage(pick);
    setShow(true);
    localStorage.setItem(LS_KEY, today);
  }, [user]);

  const dismiss = () => setShow(false);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-50 backdrop-blur-xl bg-white/40 flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={dismiss}
          data-testid="welcome-overlay"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 30, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 10, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            className="relative w-full max-w-md rounded-[2rem] overflow-hidden border border-kawaii-border p-8 md:p-10"
            style={{
              background: 'linear-gradient(135deg, #DDCEED 0%, #FBF8FE 45%, #F5D5C8 100%)'
            }}
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-kawaii-lavender/40 blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-kawaii-pink/40 blur-3xl" />

            <button
              onClick={dismiss}
              data-testid="dismiss-welcome-btn"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/70 backdrop-blur border border-white/60 flex items-center justify-center text-kawaii-text hover:bg-white"
            >
              <X className="w-4 h-4" strokeWidth={2.5} />
            </button>

            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-2xl bg-white/70 flex items-center justify-center shadow-sm">
                  <Sparkles className="w-5 h-5 text-kawaii-pink" strokeWidth={2.5} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.28em] text-kawaii-textSoft font-bold">for today</span>
              </div>

              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-kawaii-text leading-tight" data-testid="welcome-heading">
                Hi, {user?.name || "friend"} ♡
              </h2>
              <p className="mt-2 text-sm text-kawaii-textSoft font-body">So glad you're here.</p>

              <div className="mt-6 p-5 rounded-2xl bg-white/70 backdrop-blur border border-white/60">
                <p className="font-body text-base text-kawaii-text leading-relaxed italic" data-testid="welcome-message">
                  “{message}”
                </p>
              </div>

              <button
                onClick={dismiss}
                data-testid="welcome-continue-btn"
                className="kawaii-btn mt-6 w-full py-3 rounded-full bg-gradient-to-r from-kawaii-lavender to-kawaii-pink text-white font-heading font-semibold shadow-md"
              >
                Enter MyLymphieLife
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
